import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, X } from '@phosphor-icons/react';
import { isStandalone, pushSupported, subscribeToPush } from '@/lib/pwa';
import { track } from '@/lib/track';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DISMISS_KEY = 'nos_notif_prompt_v1';

// One-time notification opt-in card. Shows ONLY inside the installed PWA
// (standalone) — never on the web — on first open for a logged-in user who
// hasn't decided on notifications yet.
const NotificationPrompt = ({ user }) => {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isStandalone()) return; // installed app only — never on the web
    if (!user || !pushSupported()) return;
    if (Notification.permission !== 'default') return; // already granted/denied
    if (localStorage.getItem(DISMISS_KEY)) return; // already asked once
    // Show whenever permission is still undecided — don't let a stale push
    // subscription (e.g. left over on Android after a permission reset) hide it.
    setShow(true);
    // The opt-in funnel behind the daily push campaigns: how many people are
    // asked, how many accept. Without this the campaigns run blind.
    track('push_prompt_shown', { surface: 'standalone_pwa' });
  }, [user]);

  const enable = async () => {
    setBusy(true);
    track('push_enable_click');
    try {
      await subscribeToPush(API, axios);
      track('push_permission_granted');
      toast.success("You're in - we'll ping you when new fits drop.");
    } catch (e) {
      // Blocked at the OS prompt and failed-to-subscribe are different
      // problems — one is a user decision, the other is a bug to chase.
      if (e.message === 'denied') {
        track('push_permission_denied');
        toast.error('Notifications were blocked.');
      } else {
        track('push_subscribe_failed', { error_message: e?.message });
        toast.error('Could not enable notifications.');
      }
    } finally {
      localStorage.setItem(DISMISS_KEY, '1'); // don't ask again either way
      setBusy(false);
      setShow(false);
    }
  };

  const dismiss = () => {
    track('push_prompt_dismiss');
    localStorage.setItem(DISMISS_KEY, '1');
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[90] p-4 flex justify-center" data-testid="notif-prompt">
      <div className="w-full max-w-md bg-white border border-[#E4E1D9] rounded-2xl shadow-2xl p-4 flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-full bg-[#F0EFEB] flex items-center justify-center">
          <Bell size={20} weight="fill" className="text-[#2D302A]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#2D302A]">Get new-fit alerts</p>
          <p className="text-xs text-[#6B6A68] mt-0.5">
            Be the first to know when a fit drops in your size.
          </p>
          <div className="flex items-center gap-3 mt-3">
            <button
              onClick={enable}
              disabled={busy}
              className="bg-[#2D302A] text-white text-xs uppercase tracking-[0.15em] px-4 py-2 rounded-md hover:bg-[#1A1C18] transition-colors disabled:opacity-50"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              data-testid="notif-prompt-enable"
            >
              {busy ? 'Enabling…' : 'Enable'}
            </button>
            <button
              onClick={dismiss}
              className="text-xs text-[#6B6A68] hover:underline"
              data-testid="notif-prompt-dismiss"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="shrink-0 text-[#6B6A68] hover:text-[#1A1A1A] transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

export default NotificationPrompt;
